import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { ArrowRight, Check, Star, Users, Briefcase, Building2, Landmark, Sparkles, MessageCircle, GitBranch, TrendingUp, Loader2 } from "lucide-react";

const TIERS = [
  {
    key: "starter", name: "Starter", icon: Users,
    price: "$25", per: "/Portfolio/mo", sub: "Free for 2 weeks, then $25/Portfolio/mo",
    blurb: "Solo PMs, one initiative — pay only for what you run.",
    features: [
      "$25 per active Portfolio/mo · up to 3 Projects each — no flat fee",
      "One Portfolio costs $25/mo, two costs $50/mo, and so on",
      "Unlimited seats & guests",
      "Unlimited Cards",
      "Auto-pilot % complete",
      "10 Briefing credits/mo",
    ],
    cta: "Start 2-Week Free Trial", popular: false,
  },
  {
    key: "team", name: "Team", icon: Briefcase,
    price: "$249", per: "/mo", sub: "Built for smaller companies",
    blurb: "One flat price for a small company's whole slate.",
    features: [
      "Up to 5 Portfolios · up to 10 Projects each",
      "Everything in Starter",
      "Flat monthly price — no per-Portfolio math",
      "Cross-portfolio rollups",
      "15 Briefing credits/mo",
    ],
    cta: "Start Trial", popular: false,
  },
  {
    key: "growth", name: "Growth", icon: TrendingUp,
    price: "$449", per: "/mo per department", sub: "Most Popular",
    blurb: "Teams running multiple initiatives.",
    features: [
      "Up to 10 Portfolios · up to 15 Projects each",
      "Everything in Team",
      "Branded Briefings",
      "25 Briefing credits/mo",
      "Extra Portfolios à la carte — $59/mo each, up to 15 total",
    ],
    note: "Scoped to one department or business unit, like Command — running Growth in more than one independently-budgeted department? Each gets its own subscription.",
    cta: "Start Trial", popular: true,
  },
  {
    key: "command", name: "Command", icon: Building2,
    price: "$649", per: "/mo", sub: "One department, serious scale.",
    blurb: "One department, serious scale.",
    features: [
      "Up to 25 Portfolios · unlimited Projects each",
      "Everything in Growth",
      "SSO, audit trail, permissions",
      "Unlimited Briefings",
      "Dedicated success manager",
      "Storage: unlimited, fair use — bundled in, never metered",
      "Optional add-ons: Orchestra Pit, Decision Analysis",
    ],
    note: "Scoped to one department or business unit. Running more than one independently-budgeted department? Each gets its own Command subscription, or roll them into an Enterprise agreement.",
    cta: "Talk to Sales", popular: false,
  },
  {
    key: "enterprise", name: "Enterprise", icon: Landmark,
    price: "Custom", per: "", sub: "Multi-department rollouts, negotiated terms.",
    blurb: "The only tier with unlimited Portfolios.",
    features: [
      "Unlimited Portfolios & Projects — the only tier without caps",
      "Everything in Command",
      "Priced per department, not per company — each department billed on its own Portfolio band, consolidated into one contract",
      "Volume discount for bundling multiple departments versus buying Command separately per department",
      "Custom SLAs, compliance review, and storage terms",
      "Dedicated implementation & CSM team",
      "One contract, one renewal date — pricing still reflects how many departments are actually using it",
    ],
    note: "Illustrative: a 3-department rollout starts around $1,500/mo total ($500/department minimum) — actual quotes vary by Portfolio volume and department count.",
    cta: "Contact Sales", popular: false,
  },
];

const BUNDLES = [
  ["Starter Top-Up", "10", "$25", "$2.50"],
  ["Small Bundle", "25", "$50", "$2.00"],
  ["Medium Bundle", "50", "$90", "$1.80"],
  ["Large Bundle", "100", "$160", "$1.60"],
  ["Enterprise Bundle", "200", "$280", "$1.40"],
];

const COMPARE = [
  ["Add a stakeholder", "Costs extra", "Free (capped)", "Always free"],
  ["Price scales with", "Team size", "Nothing", "Portfolios"],
  ["Occasional use", "Full plan required", "Full plan required", "Pay-per-Briefing"],
  ["Storage", "Metered tiers", "Metered tiers", "Unlimited, fair use"],
];

const FAQ = [
  ["Why no per-seat pricing?", "The people who just need visibility shouldn't be why your bill grows."],
  ["What's a Portfolio?", "The top-level container rolling up your Projects, Spaces, and Cards into one view — usually a program or initiative."],
  ["What's a Briefing credit?", "One credit = one status report exported or shared outside P.M.O. Viewing dashboards is always free."],
  ["Over my Portfolio limit?", "We'll tell you first. Upgrade, add a Portfolio à la carte (Growth), or archive — no surprise charges."],
  ["Why does each tier cap Projects per Portfolio?", "A Portfolio is a rollup, not an unlimited bucket. Project caps per Portfolio (3 on Starter, 10 on Team, 15 on Growth, unlimited on Command and Enterprise) keep the view meaningful. Active Portfolio caps are enforced in-app too: 3 on Starter/trial, 5 on Team, 10 on Growth, 25 on Command, unlimited on Enterprise."],
  ["Is Starter really free?", "Your first 2 weeks are fully free, no strings attached. After the trial, Starter is $25 per active Portfolio/mo — no flat fee — with 10 Briefing credits included. Cancel anytime before the trial ends if it's not for you."],
  ["Do I pay extra for storage or maintenance?", "No. Storage is unlimited under fair use and maintenance is part of your subscription — no separate line items, ever. Enterprise customers with unusual storage needs can set custom terms in contract."],
  ["We have multiple departments — do we need a subscription for each?", "Yes, if they're independently budgeted. Growth and Command are both scoped to one department or business unit, so separate P&Ls each need their own subscription. If you'd rather consolidate, Enterprise wraps every department into one contract and one renewal date — but the price still reflects how many departments are actually on the platform, at a volume discount versus buying Growth or Command separately for each. What you get is billing simplicity, not a way around per-department pricing."],
];

export default function Pricing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [buying, setBuying] = useState("");

  const handleCta = async (t) => {
    const paid = ["team", "growth", "command"].includes(t.key);
    if (!paid) { nav(t.key === "enterprise" || t.key === "command" ? "/register" : "/register"); return; }
    if (!user) { nav("/register"); return; }
    setBuying(t.key);
    try {
      const { data } = await api.post("/payments/checkout", { plan: t.key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e, "Could not start checkout"));
      setBuying("");
    }
  };
  return (
    <div className="min-h-screen bg-[#050b20] text-white">
      <div aria-hidden className="pointer-events-none fixed -top-40 -left-40 h-[34rem] w-[34rem] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.22), transparent 70%)" }} />
      <div className="relative z-10">
        <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050b20]/70 backdrop-blur">
          <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
            <button onClick={() => nav("/")} className="font-display text-2xl font-extrabold tracking-tight text-white" data-testid="pricing-brand-button">
              Project Management <span className="text-[#7ea6ff]">Orchestrator</span>
            </button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white" onClick={() => nav("/login")} data-testid="pricing-signin-button">Sign in</Button>
              <Button className="gap-2" onClick={() => nav("/register")} data-testid="pricing-getstarted-button">Get started <ArrowRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="max-w-[1100px] mx-auto px-6 pt-16 pb-10 text-center">
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="pricing-title">
            Pricing that scales with your portfolios. <span className="text-[#7ea6ff]">Not your people.</span>
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">Every seat is free — forever. You pay for the portfolios you run and the briefings you send.</p>
          <Button size="lg" className="mt-7 gap-2 h-12 px-7" onClick={() => nav("/register")} data-testid="pricing-hero-cta">Start Free — Your First 2 Weeks Are On Us <ArrowRight className="h-4 w-4" /></Button>
          <p className="mt-8 text-sm text-slate-400 max-w-3xl mx-auto border border-white/10 rounded-xl p-4 bg-white/[0.03]">
            <b className="text-white">The Pitch:</b> Other tools charge per seat, so you invite fewer people — and your reports lose their audience. P.M.O. flips that: unlimited seats, always free. You pay for oversight, not attendance.
          </p>
        </section>

        {/* Tiers */}
        <section className="max-w-[1200px] mx-auto px-6 pb-14">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {TIERS.map((t) => (
              <div key={t.key} className={`relative flex flex-col rounded-2xl border p-6 bg-white/[0.04] ${t.popular ? "border-[#7ea6ff] shadow-[0_0_40px_rgba(126,166,255,0.15)]" : "border-white/10"}`} data-testid={`pricing-tier-${t.key}`}>
                {t.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold px-3 py-1 rounded-full bg-[#7ea6ff] text-[#050b20] inline-flex items-center gap-1"><Star className="h-3 w-3" /> Most Popular</span>}
                <t.icon className="h-6 w-6 text-[#7ea6ff]" />
                <h3 className="font-display text-xl font-extrabold mt-3">{t.name}</h3>
                <p className="text-xs text-slate-400 mt-0.5">{t.sub}</p>
                <div className="mt-3">
                  <span className="font-display text-3xl font-extrabold">{t.price}</span>
                  <span className="text-sm text-slate-400">{t.per}</span>
                </div>
                <p className="text-sm text-slate-300 mt-1">{t.blurb}</p>
                <ul className="mt-4 space-y-2 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13px] text-slate-200"><Check className="h-3.5 w-3.5 text-emerald-400 shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
                {t.note && <p className="mt-3 text-[11px] text-slate-400 border-t border-white/10 pt-2">{t.note}</p>}
                <Button className={`mt-4 w-full ${t.popular ? "" : "bg-white/10 hover:bg-white/20"}`} disabled={buying === t.key} onClick={() => handleCta(t)} data-testid={`pricing-cta-${t.key}`}>{buying === t.key ? <Loader2 className="h-4 w-4 animate-spin" /> : (["team", "growth", "command"].includes(t.key) && user ? "Choose plan" : t.cta)}</Button>
              </div>
            ))}
          </div>
        </section>

        {/* Briefing credits */}
        <section className="max-w-[900px] mx-auto px-6 pb-14">
          <h2 className="font-display text-2xl font-extrabold flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#7ea6ff]" /> Maestro Briefing Credits</h2>
          <p className="mt-2 text-sm text-slate-300">Need reports, not the whole rig? A Maestro Briefing is your auto-generated, exec-ready status report — built by Project Maestro, no manual updates, no all-nighters.</p>
          <p className="mt-2 text-sm text-slate-400">Every tier includes a monthly allotment (Starter: 10, Team: 15, Growth: 25, Command: unlimited). Need just one or two more? Pay $9/Briefing, on demand. Need a bunch? Buy a bundle — the bigger the bundle, the lower your per-credit rate.</p>
          <div className="mt-5 overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm" data-testid="pricing-bundles-table">
              <thead><tr className="bg-white/[0.06] text-left text-xs uppercase tracking-wide text-slate-300">
                <th className="px-4 py-2.5">Bundle</th><th className="px-4 py-2.5">Credits</th><th className="px-4 py-2.5">Price</th><th className="px-4 py-2.5">Price/Credit</th>
              </tr></thead>
              <tbody>
                {BUNDLES.map((b) => (
                  <tr key={b[0]} className="border-t border-white/10">
                    <td className="px-4 py-2.5 font-semibold">{b[0]}</td><td className="px-4 py-2.5">{b[1]}</td><td className="px-4 py-2.5">{b[2]}</td><td className="px-4 py-2.5 text-emerald-400 font-semibold">{b[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[13px] text-slate-400 italic">Like buying signature-request credits in bulk — you pay when something real gets delivered, not for who reads it, and you save more the more you send.</p>
        </section>

        {/* Add-ons */}
        <section className="max-w-[900px] mx-auto px-6 pb-14">
          <h2 className="font-display text-2xl font-extrabold">Power Add-Ons <span className="text-sm font-normal text-slate-400">(Command & Enterprise)</span></h2>
          <p className="mt-1 text-sm text-slate-400">Most teams never need these. The ones that do, need them badly.</p>
          <div className="mt-5 grid sm:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6" data-testid="pricing-addon-pit">
              <MessageCircle className="h-5 w-5 text-[#7ea6ff]" />
              <h3 className="font-display font-extrabold mt-2">Orchestra Pit — $199/mo flat</h3>
              <p className="text-sm text-slate-300 mt-1.5">The live, cross-portfolio conductor's view. Every Portfolio, every Briefing, one screen — built for leadership standups, not status hunting.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6" data-testid="pricing-addon-decisions">
              <GitBranch className="h-5 w-5 text-[#7ea6ff]" />
              <h3 className="font-display font-extrabold mt-2">Decision Analysis — $29/Insight credit, or $299/mo unlimited</h3>
              <p className="text-sm text-slate-300 mt-1.5">Run the what-if before you commit the resources. Scenario modeling on timelines, budgets, and staffing, priced like Briefings: pay per use, or go unlimited.</p>
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section className="max-w-[900px] mx-auto px-6 pb-14">
          <h2 className="font-display text-2xl font-extrabold">Why It's Different</h2>
          <div className="mt-5 overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm" data-testid="pricing-compare-table">
              <thead><tr className="bg-white/[0.06] text-left text-xs uppercase tracking-wide text-slate-300">
                <th className="px-4 py-2.5"></th><th className="px-4 py-2.5">Per-Seat Tools</th><th className="px-4 py-2.5">Flat-Rate Tools</th><th className="px-4 py-2.5 text-[#7ea6ff]">P.M.O.</th>
              </tr></thead>
              <tbody>
                {COMPARE.map((r) => (
                  <tr key={r[0]} className="border-t border-white/10">
                    <td className="px-4 py-2.5 font-semibold">{r[0]}</td><td className="px-4 py-2.5 text-slate-400">{r[1]}</td><td className="px-4 py-2.5 text-slate-400">{r[2]}</td><td className="px-4 py-2.5 text-emerald-400 font-semibold">{r[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-[850px] mx-auto px-6 pb-16">
          <h2 className="font-display text-2xl font-extrabold">FAQ</h2>
          <div className="mt-5 space-y-3">
            {FAQ.map(([q, a]) => (
              <details key={q} className="group rounded-xl border border-white/10 bg-white/[0.04] px-5 py-3.5" data-testid={`pricing-faq-${q.slice(0, 18).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <summary className="cursor-pointer font-semibold text-[15px] list-none flex items-center justify-between">{q}<span className="text-slate-500 group-open:rotate-45 transition-transform text-lg leading-none">+</span></summary>
                <p className="mt-2 text-sm text-slate-300">{a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Closing */}
        <section className="max-w-[850px] mx-auto px-6 pb-20 text-center">
          <h2 className="font-display text-3xl font-extrabold">Stop paying to loop people in.</h2>
          <p className="mt-2 text-slate-300">Bring your whole team, free. Pay for the portfolios you run.</p>
          <Button size="lg" className="mt-6 gap-2 h-12 px-7" onClick={() => nav("/register")} data-testid="pricing-closing-cta">Start Free — Your First 2 Weeks Are On Us <ArrowRight className="h-4 w-4" /></Button>
        </section>

        <footer className="border-t border-white/10">
          <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between text-sm text-slate-400">
            <span className="font-display font-bold text-white">Project Management <span className="text-[#7ea6ff]">Orchestrator</span></span>
            <div className="flex items-center gap-4">
              <button onClick={() => nav("/pricing")} className="hover:text-white" data-testid="footer-pricing-link">Pricing</button>
              <button onClick={() => nav("/terms")} className="hover:text-white" data-testid="footer-terms-link">Terms</button>
              <button onClick={() => nav("/privacy")} className="hover:text-white" data-testid="footer-privacy-link">Privacy</button>
            </div>
          </div>
          <p className="mt-4 text-[11px] text-slate-500 max-w-3xl" data-testid="pricing-pmi-disclaimer">
            Project Management Orchestrator is an independent product of Planit Orbit, LLC and is not affiliated with, sponsored by, or endorsed by the Project Management Institute, Inc. (PMI).
          </p>
        </footer>
      </div>
    </div>
  );
}
